import { useEffect, useMemo, useState } from 'react';
import type { BulkSendResultDto, EmailTemplate, StoredFile } from '@epasu/core';
import { Search, Send, X } from 'lucide-react';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnhangWahl } from '@/components/ReplyDialog';
import { ErstanspracheHaken, useErstansprache } from '@/components/ErstanspracheHaken';
import { PostfachWahl, useAktivePostfaecher } from '@/components/PostfachWahl';
import { RichTextArea } from '@/components/RichTextArea';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';

interface Props {
  /** Vorausgewählter Empfänger, sonst wird einer gesucht. */
  contact?: ContactRow | null;
  /**
   * Vorausgewählte Empfänger — etwa alle Ansprechpartner einer Firma.
   * Anders als `contact` bleiben sie änderbar.
   */
  contacts?: ContactRow[];
  onClose: () => void;
  onSent?: () => void;
}

/**
 * Eine neue E-Mail schreiben, von überall aus.
 *
 * Bisher ging Schreiben nur über die Detailseite eines Kontakts — wer eine
 * Mail loswerden wollte, musste erst den Kunden suchen und aufrufen. Hier wird
 * der Empfänger im Dialog selbst gewählt.
 *
 * Kontakte ohne E-Mail-Adresse und abgemeldete tauchen in der Auswahl gar nicht
 * erst auf: An sie kann nichts rausgehen, und eine Auswahl anzubieten, die
 * anschließend abgelehnt wird, ist nur ärgerlich.
 */
export function ComposeDialog({ contact, contacts, onClose, onSent }: Props) {
  const [empfaenger, setEmpfaenger] = useState<ContactRow[]>(
    contact ? [contact] : (contacts ?? []),
  );
  const [suche, setSuche] = useState('');

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('{{anrede}},\n\n');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [bibliothek, setBibliothek] = useState<StoredFile[]>([]);
  const [anhaenge, setAnhaenge] = useState<string[]>([]);

  // Ohne Vorgabe: das Standardkonto. Dieselbe Abfrage sagt auch, ob überhaupt
  // ein Postfach eingerichtet ist — das gehört vorher gesagt, nicht erst nach
  // dem Klick auf „Senden“.
  const absender = useAktivePostfaecher();
  const kontoFehlt = absender.geladen && absender.konten.length === 0;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bericht, setBericht] = useState<BulkSendResultDto | null>(null);

  useEffect(() => {
    void crm.templates.list().then(setTemplates);
    void crm.files.list({ libraryOnly: true }).then(setBibliothek);
  }, []);

  // Gesucht wird auf dem Server statt in einer einmal geladenen Liste — bei
  // einigen hundert Kontakten fehlte der gesuchte sonst schlicht, ohne dass
  // etwas darauf hinwiese.
  const {
    data: kontakteRoh,
    loading: ladeKontakte,
    error: sucheFehler,
  } = useDebouncedSearch(
    () => crm.contacts.list({ limit: 20, ...(suche.trim() ? { search: suche.trim() } : {}) }),
    [suche],
  );
  // Kontakte ohne E-Mail-Adresse und abgemeldete tauchen gar nicht erst auf:
  // An sie kann nichts rausgehen.
  const kontakte = useMemo(
    () => (kontakteRoh ?? []).filter((c) => c.email && !c.optedOut),
    [kontakteRoh],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const gewaehlteIds = useMemo(
    () => new Set(empfaenger.map((e) => e.id)),
    [empfaenger],
  );

  // Folgt der Auswahl, solange niemand das Häkchen von Hand angefasst hat.
  const erstansprache = useErstansprache(empfaenger.map((e) => e.id));

  // Wer schon als Empfänger dabei ist, muss kein zweites Mal im Ergebnis
  // auftauchen.
  const treffer = useMemo(
    () => kontakte.filter((c) => !gewaehlteIds.has(c.id)),
    [kontakte, gewaehlteIds],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (empfaenger.length === 0) {
      setError('Bitte mindestens einen Empfänger wählen.');
      return;
    }
    if (!subject.trim()) {
      setError('Bitte einen Betreff angeben.');
      return;
    }

    setBusy(true);
    setError(null);
    setBericht(null);
    try {
      const res = await crm.mail.sendBulk({
        contactIds: empfaenger.map((c) => c.id),
        subject,
        body,
        fileIds: anhaenge,
        ...(absender.konto ? { accountId: absender.konto } : {}),
        // Gesetzt gilt fuer alle Empfaenger dieses Versands, ungesetzt fuer
        // keinen — der Vertrag kennt hier bewusst keine Auswahl je Empfaenger.
        outreach: erstansprache.aktiv,
      });

      onSent?.();

      // Ging alles raus, schliesst der Dialog. Gab es Fehlschlaege, bleibt er
      // offen und zeigt, wen es nicht erreicht hat — sonst merkt es niemand.
      if (res.failed === 0) {
        onClose();
        return;
      }
      setBericht(res);
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Versand fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Neue E-Mail"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-8 w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">Neue E-Mail</h2>

        {kontoFehlt && (
          <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            Es ist kein Postfach eingerichtet — bis dahin geht nichts raus. Unter
            Einstellungen ein Postfach anlegen.
          </p>
        )}

        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Empfänger
              </span>
              {empfaenger.length > 1 && (
                <span className="text-xs text-muted-foreground">
                  {empfaenger.length} Empfänger — jeder bekommt eine eigene Mail
                </span>
              )}
            </div>

            {empfaenger.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {empfaenger.map((c) => (
                  <span
                    key={c.id}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs"
                  >
                    {c.displayName}
                    <button
                      type="button"
                      aria-label={`${c.displayName} entfernen`}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() =>
                        setEmpfaenger((liste) => liste.filter((x) => x.id !== c.id))
                      }
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Empfänger suchen und hinzufügen …"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
              />
            </div>

            {(suche.trim() || empfaenger.length === 0) && (
              <div className="max-h-44 overflow-y-auto rounded-md border">
                {ladeKontakte ? (
                  <p className="p-4 text-sm text-muted-foreground">Lade …</p>
                ) : sucheFehler ? (
                  <p className="p-4 text-sm text-destructive">{sucheFehler}</p>
                ) : treffer.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">
                    {suche.trim()
                      ? 'Kein Treffer.'
                      : 'Kein Kontakt mit E-Mail-Adresse gefunden.'}
                  </p>
                ) : (
                  <>
                    {suche.trim() && treffer.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setEmpfaenger((liste) => [...liste, ...treffer]);
                          setSuche('');
                        }}
                        className="block w-full border-b bg-muted/40 px-3 py-2 text-left text-sm font-medium hover:bg-muted"
                      >
                        Alle {treffer.length} Treffer hinzufügen
                      </button>
                    )}
                    {treffer.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setEmpfaenger((liste) => [...liste, c]);
                          setSuche('');
                        }}
                        className="block w-full border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/50"
                      >
                        {c.displayName}
                        <span className="text-muted-foreground"> · {c.email}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/*
            Steht direkt unter den Empfängern, weil sich die Zahl darunter auf
            genau sie bezieht — weiter unten wäre nicht mehr zu sehen, worauf
            sich das Häkchen bezieht.
          */}
          <ErstanspracheHaken zustand={erstansprache} />

          <PostfachWahl
            konten={absender.konten}
            wert={absender.konto}
            onChange={absender.setKonto}
          />

          {templates.length > 0 && (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Vorlage einfügen
              </span>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                defaultValue=""
                onChange={(e) => {
                  const t = templates.find((x) => x.id === e.target.value);
                  if (!t) return;
                  if (!subject.trim()) setSubject(t.subject);
                  setBody(t.body);
                  e.currentTarget.value = '';
                }}
              >
                <option value="">— keine —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Betreff</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>

          <div className="space-y-1.5">
            <RichTextArea label="Text" value={body} onChange={setBody} />
            <span className="text-xs text-muted-foreground">
              Platzhalter wie <code>{'{{anrede}}'}</code> werden beim Senden gesetzt.
            </span>
          </div>

          {bibliothek.length > 0 && (
            <AnhangWahl
              dateien={bibliothek}
              gewaehlt={anhaenge}
              onChange={setAnhaenge}
            />
          )}
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        {bericht && (
          <div className="mt-4 rounded-md border p-3 text-sm">
            <p className="font-medium">
              {bericht.sent} versandt, {bericht.failed} fehlgeschlagen
            </p>
            <ul className="mt-2 space-y-1">
              {bericht.results
                .filter((r) => !r.ok)
                .map((r) => (
                  <li key={r.contactId} className="text-destructive">
                    {r.name}: {r.error ?? 'unbekannter Fehler'}
                  </li>
                ))}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">
              Die erfolgreich versandten Mails sind raus — ein erneutes Senden
              würde sie doppelt zustellen.
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {bericht ? 'Schließen' : 'Abbrechen'}
          </Button>
          <Button type="submit" disabled={busy || empfaenger.length === 0}>
            <Send className="mr-2 h-4 w-4" />
            {busy
              ? 'Sende …'
              : empfaenger.length > 1
                ? `An ${empfaenger.length} senden`
                : 'Senden'}
          </Button>
        </div>
      </form>
    </div>
  );
}
